import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Handshake, FileText, Upload } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const itDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const plusOneYear = (d) => {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  if (isNaN(dt)) return "";
  dt.setFullYear(dt.getFullYear() + 1);
  return dt.toISOString().slice(0, 10);
};

export default function ConvertPartnerDialog({ partner, prospect, onOpenChange, onDone }) {
  const entity = partner || prospect || null;
  const open = !!entity;
  const isProspect = !!prospect && !partner;
  const isRenewal = !!partner?.is_partner;
  const [dataFirma, setDataFirma] = useState("");
  const [categoria, setCategoria] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!entity) { setFile(null); return; }
    const fd = entity.fier_dettaglio || {};
    const def = (entity.partnership_contract?.data_firma || fd.data_attivazione || new Date().toISOString().slice(0, 10)).slice(0, 10);
    setDataFirma(def);
    setCategoria(entity.partner_categoria || "");
    setFile(null);
  }, [entity]);

  const submit = async () => {
    if (!file) { toast.error("Carica il PDF del contratto firmato"); return; }
    if (!categoria) { toast.error("Seleziona la categoria del partner"); return; }
    setSaving(true);
    try {
      let partnerId = partner?.id;
      if (isProspect) {
        partnerId = prospect.converted_partner_id;
        if (!partnerId) {
          const rc = await api.post(`/prospects/${prospect.id}/convert-cliente`);
          partnerId = rc.data.partner_id;
        }
      }
      const fdd = new FormData();
      fdd.append("file", file);
      if (dataFirma) fdd.append("data_firma", dataFirma);
      fdd.append("categoria", categoria);
      const r = await api.post(`/partners/${partnerId}/convert-partner`, fdd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data?.message || (isRenewal ? "Contratto rinnovato" : "Convertito in Partner"));
      onOpenChange?.(false);
      onDone?.(partnerId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onOpenChange?.(false); }}>
      <DialogContent className="sm:max-w-md" data-testid="convert-partner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Handshake className="h-5 w-5 text-green-600" />{isRenewal ? "Rinnova contratto partnership" : "Converti in Partner"}</DialogTitle>
          <DialogDescription>
            {entity?.name} — è obbligatorio allegare il <b>PDF del contratto firmato</b>. La durata è di 1 anno: la scadenza viene calcolata dalla data firma.
            {isProspect && <span className="mt-1 block text-[11px] text-muted-foreground">Il prospect verrà prima trasformato in anagrafica cliente, poi promosso a Partner.</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Data firma <span className="text-[10px] text-muted-foreground">(da gestionale Fier, modificabile)</span></Label>
            <Input type="date" value={dataFirma} onChange={(e) => setDataFirma(e.target.value)} data-testid="convert-data-firma" />
            <p className="text-[11px] text-muted-foreground">Scadenza automatica: <b>{itDate(plusOneYear(dataFirma))}</b></p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Categoria partner *</Label>
            <Select value={categoria} onValueChange={setCategoria}>
              <SelectTrigger data-testid="convert-categoria"><SelectValue placeholder="Seleziona categoria…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Installatore">Installatore</SelectItem>
                <SelectItem value="Progettista">Progettista</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Contratto firmato (PDF) *</Label>
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid="convert-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <Button type="button" variant="outline" className="w-full justify-start" onClick={() => fileRef.current?.click()} data-testid="convert-file-btn">
              {file ? <FileText className="mr-2 h-4 w-4 text-green-600" /> : <Upload className="mr-2 h-4 w-4" />}
              <span className="truncate">{file ? file.name : "Seleziona PDF…"}</span>
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving || !file || !categoria} data-testid="convert-submit-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRenewal ? "Rinnova" : "Converti in Partner")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
